<script setup lang="ts" generic="T extends number">
import { ref } from 'vue';

import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';
import type { AppSvgIconColor } from '../appSvgIcon/AppSvgIcon.model';
import { getCssCustomPropertyValue } from '../../helpers/cssCustomProperties';
import AppRawButton from '../appRawButton/AppRawButton.vue';

interface AppStarsSelectorProps<T extends number> {
  options: readonly T[];
  selected: T[];
}

const props = defineProps<AppStarsSelectorProps<T>>();

interface AppStarsSelectorEmits {
  (eventName: 'select', selectedStart: T): void;
}

const emit = defineEmits<AppStarsSelectorEmits>();

const hoverOverStar = ref<number | undefined>(undefined);

function getStarColor(
  star: T,
  currentHoverOverStar: number | undefined
): AppSvgIconColor {
  if (currentHoverOverStar === star) {
    return props.selected.includes(star)
      ? 'color-brand-violet'
      : 'color-neutral-40';
  }

  return props.selected.includes(star)
    ? 'color-brand-violet'
    : 'color-neutral-30';
}

function getTextColor(star: T): { color: string } {
  return props.selected.includes(star)
    ? { color: getCssCustomPropertyValue('color-neutral-70') }
    : { color: getCssCustomPropertyValue('color-neutral-70') };
}
</script>

<template>
  <div class="app-stars-selector">
    <AppRawButton
      v-for="star in options"
      :key="star"
      class="star"
      @click="emit('select', star)"
      @mouseenter="hoverOverStar = star"
      @mouseleave="hoverOverStar = undefined"
      @focusin="hoverOverStar = star"
      @focusout="hoverOverStar = undefined"
    >
      <AppSvgIcon
        name="star"
        size="24"
        :color="getStarColor(star, hoverOverStar)"
      />
      <div class="start-text" :style="getTextColor(star)">
        {{ star }}
      </div>
    </AppRawButton>
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/fonts' as *;
.app-stars-selector {
  display: flex;
  gap: var(--spacing-4);
  padding: 0 var(--spacing-4);

  .star {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    gap: var(--spacing-4);

    .start-text {
      margin-top: var(--spacing-4);
      @include font-label-s-light;
    }
  }
}
</style>
